import "./styles.scss";

import { useState, useRef, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import useMergeRefs from "hooks/useMergeRefs";
import useDropdownPlacement from "hooks/useDropdownPlacement";
import useClickOutside from "libs/helpers/useClickOutside";

import { Priority, Status, Sort } from "./components";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";

const mainClass = "select";

const Select = forwardRef(
  (
    {
      variant,
      name,
      label,
      labelCta,
      options = [],
      placeholder,
      required,
      selected,
      onChange,
      searcher,
      disabled,
      value,
      touched,
      error,
      hideError,
      forceBottom,
      id,
      isFormik,
      isCompletelyDisabled,
      optionsDisabled,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const is_error = !!!hideError && touched && !!error;

    const [select_open, setSelectOpen] = useState(false);
    const [search, setSearch] = useState(null);
    const [search_result, setSearchResult] = useState([]);

    const container_ref = useRef();
    const button_ref = useRef();
    const dropdown_ref = useRef();
    const searcher_ref = useRef();

    const mergedRef = useMergeRefs(ref, container_ref);

    useClickOutside(container_ref, () => {
      setSelectOpen(false);
      setSearch(null);
      setSearchResult([]);
    });

    const open_top = useDropdownPlacement(
      button_ref,
      select_open && !isCompletelyDisabled,
      { dropdownRef: dropdown_ref, forceBottom },
    );

    const current_value = options?.find((item) => item?.value === value);

    const onSearch = () => {
      if (!!!search) return setSearchResult([]);
      const filtered_options = options?.filter(
        (item) => item?.label !== -1 || item?.value !== -1,
      );
      const find_options = filtered_options?.filter(
        (item) =>
          item?.label?.toLowerCase().indexOf(search?.toLowerCase()) !== -1 ||
          item?.value?.toLowerCase().indexOf(search?.toLowerCase()) !== -1,
      );
      setSearchResult(find_options);
    };

    useEffect(() => {
      if (!!select_open) {
        searcher_ref?.current?.focus();
      }
      // eslint-disable-next-line
    }, [select_open]);

    useEffect(() => {
      onSearch(search);
      // eslint-disable-next-line
    }, [search]);

    const options_to_show = search?.length > 1 ? search_result : options;

    return (
      <div
        ref={mergedRef}
        id={id}
        className={classnames(mainClass, {
          [`${mainClass}--active`]: !!select_open,
          [`${mainClass}--selected`]: !!selected,
          [`${mainClass}--top`]: open_top && !forceBottom,
          [`${mainClass}--disabled`]: !!disabled,
          [`${mainClass}--completely-disabled`]: isCompletelyDisabled,
          [`${mainClass}--error`]: !!is_error,
          [`${mainClass}--empty`]: !!!value,
          [`${mainClass}--${variant}`]: !!variant,
        })}
      >
        {!!label && (
          <label>
            <span>
              {label}
              {!!required && <span>*</span>}
            </span>
            {!!labelCta?.text && !isCompletelyDisabled && (
              <button type="button" onClick={labelCta?.onClick}>
                {labelCta.text}
                <i>
                  <ArrowIcon />
                </i>
              </button>
            )}
          </label>
        )}
        <button
          ref={button_ref}
          type="button"
          onClick={() => setSelectOpen((prev) => !prev)}
        >
          <span className={current_value?.variant}>
            {current_value?.icon && (
              <i>{current_value.icon === "_" ? <i /> : current_value.icon}</i>
            )}
            {current_value?.label ? (
              <span>{current_value.label}</span>
            ) : typeof placeholder === "object" ? (
              <>
                {placeholder?.icon && (
                  <i>{placeholder.icon === "_" ? <i /> : placeholder.icon}</i>
                )}
                {placeholder?.label && <span>{placeholder.label}</span>}
              </>
            ) : (
              placeholder
            )}
          </span>
          {options?.length > 1 && !isCompletelyDisabled && <ArrowIcon />}
        </button>
        {select_open && !isCompletelyDisabled && (
          <div ref={dropdown_ref} className={`${mainClass}__options`}>
            {!!searcher && (
              <div className={`${mainClass}__options__searcher`}>
                <input
                  ref={searcher_ref}
                  type="text"
                  placeholder={t("Search")}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
            <div className={`${mainClass}__options__wrapper`}>
              {options_to_show?.map((item, index) => (
                <button
                  type="button"
                  key={index}
                  className={classnames(`${mainClass}__options__item`, {
                    [`${mainClass}__options__item--selected`]:
                      selected?.value === item?.value,
                    [`${mainClass}__options__item--${item.variant}`]:
                      !!item?.variant,
                    [`${mainClass}__options__item--disabled`]:
                      optionsDisabled?.includes(item?.value),
                  })}
                  disabled={optionsDisabled?.includes(item?.value)}
                  onClick={() => {
                    if (typeof onChange === "function")
                      onChange(
                        isFormik
                          ? { target: { name, value: item?.value } }
                          : { ...item, label: item?.label, value: item?.value },
                      );

                    setSearch(null);
                    setSearchResult([]);
                    setSelectOpen((prev) => !prev);
                  }}
                >
                  {!!item?.icon && (
                    <div>{item.icon === "_" ? <i /> : item.icon}</div>
                  )}
                  {item?.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {!!is_error && (
          <small className={`${mainClass}__error`}>{t(error)}</small>
        )}
      </div>
    );
  },
);

Select.Priority = Priority;
Select.Status = Status;
Select.Sort = Sort;

export default Select;
